/**
 * Product Image Lightbox
 * Handles opening images in fullscreen modal with slider for multiple images
 * Design matches: https://airportag.com reference website
 */

class ProductLightbox {
  constructor() {
    this.lightboxHTML = null;
    this.currentIndex = 0;
    this.images = [];
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Create lightbox modal HTML
    this.createLightboxModal();
    
    // Attach click handlers to all lightbox triggers
    this.attachTriggers();
  }

  createLightboxModal() {
    const lightboxHTML = `
      <div id="product-lightbox" class="product-lightbox" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: #fff; z-index: 9999; align-items: center; justify-content: center;">
        
        <!-- Close Button - Top Right -->
        <button class="lightbox-close" style="position: absolute; top: 20px; right: 20px; background: #fff; border: 1px solid rgba(37, 38, 39, 0.12); color: #252627; width: 56px; height: 56px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 10002; transition: all 0.2s ease;" aria-label="Close">
          <svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" style="pointer-events: none;">
            <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"></path>
            <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"></path>
            <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"></path>
          </svg>
        </button>
        
        <!-- Previous Arrow - Left Side -->
        <button class="lightbox-prev" style="position: absolute; left: 20px; top: 50%; transform: translateY(-50%); background: rgba(255, 255, 255, 0.9); border: 1px solid rgba(37, 38, 39, 0.12); color: #252627; width: 56px; height: 56px; border-radius: 50%; cursor: pointer; font-size: 24px; display: none; align-items: center; justify-content: center; z-index: 10002; transition: all 0.2s ease;" aria-label="Previous image">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="pointer-events: none;">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        
        <!-- Next Arrow - Right Side -->
        <button class="lightbox-next" style="position: absolute; right: 20px; top: 50%; transform: translateY(-50%); background: rgba(255, 255, 255, 0.9); border: 1px solid rgba(37, 38, 39, 0.12); color: #252627; width: 56px; height: 56px; border-radius: 50%; cursor: pointer; font-size: 24px; display: none; align-items: center; justify-content: center; z-index: 10002; transition: all 0.2s ease;" aria-label="Next image">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="pointer-events: none;">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        
        <!-- Image Container with Hover Close Icon -->
        <div class="lightbox-content" style="width: 100%; height: 100%; max-width: 90%; max-height: 90%; display: flex; align-items: center; justify-content: center; position: relative;">
          <div class="lightbox-image-wrapper" style="position: relative; display: inline-block; max-width: 100%; max-height: 100%;">
            <img class="lightbox-image" src="" alt="" style="max-width: 100%; max-height: 90vh; object-fit: contain; display: block;">
            
            <!-- Hover Close Icon (Minus) - Shows on image hover -->
            <div class="lightbox-hover-close" style="position: absolute; bottom: 20px; right: 20px; width: 56px; height: 56px; background: rgba(255, 255, 255, 0.9); border: 1px solid rgba(37, 38, 39, 0.12); border-radius: 50%; display: none; align-items: center; justify-content: center; pointer-events: none; z-index: 10003;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19" stroke="#252627" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
          </div>
        </div>
        
        <!-- Counter - Bottom Center -->
        <div class="lightbox-counter" style="position: absolute; bottom: 30px; left: 50%; transform: translateX(-50%); background: rgba(255, 255, 255, 0.9); border: 1px solid rgba(37, 38, 39, 0.12); color: #252627; font-size: 14px; padding: 10px 20px; border-radius: 100px; z-index: 10002; font-weight: 500;"></div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', lightboxHTML);
    this.lightboxHTML = document.getElementById('product-lightbox');
    
    // Get references to elements
    const closeBtn = this.lightboxHTML.querySelector('.lightbox-close');
    const prevBtn = this.lightboxHTML.querySelector('.lightbox-prev');
    const nextBtn = this.lightboxHTML.querySelector('.lightbox-next');
    const imageWrapper = this.lightboxHTML.querySelector('.lightbox-image-wrapper');
    const hoverClose = this.lightboxHTML.querySelector('.lightbox-hover-close');
    
    // Attach event listeners
    closeBtn.addEventListener('click', () => this.close());
    prevBtn.addEventListener('click', () => this.prev());
    nextBtn.addEventListener('click', () => this.next());
    
    // Close on background click
    this.lightboxHTML.addEventListener('click', (e) => {
      if (e.target === this.lightboxHTML) this.close();
    });
    
    // Show/hide minus icon on image hover
    imageWrapper.addEventListener('mouseenter', () => {
      hoverClose.style.display = 'flex';
    });
    
    imageWrapper.addEventListener('mouseleave', () => {
      hoverClose.style.display = 'none';
    });
    
    // Click on image or hover icon to close
    imageWrapper.addEventListener('click', () => this.close());
    
    // Hover effects for buttons
    [closeBtn, prevBtn, nextBtn].forEach(btn => {
      btn.addEventListener('mouseenter', () => {
        btn.style.background = 'rgba(255, 255, 255, 1)';
        btn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      });
      btn.addEventListener('mouseleave', () => {
        if (btn === closeBtn) {
          btn.style.background = '#fff';
        } else {
          btn.style.background = 'rgba(255, 255, 255, 0.9)';
        }
        btn.style.boxShadow = 'none';
      });
    });
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (this.lightboxHTML.style.display === 'flex') {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      }
    });
  }

  attachTriggers() {
    // Find all lightbox trigger buttons
    const triggers = document.querySelectorAll('.js-lightbox-trigger');
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Get all product images
        const mediaGallery = trigger.closest('.media-gallery');
        if (mediaGallery) {
          const allImages = mediaGallery.querySelectorAll('.media-viewer__item[data-media-type="image"] .product-image');
          this.images = Array.from(allImages).map(img => ({
            src: img.src.replace(/(_\d+x\d+|\?.*)/g, ''), // Get full resolution
            alt: img.alt || 'Product image'
          }));
          
          // Find current image index
          const clickedImage = trigger.querySelector('.product-image');
          this.currentIndex = Array.from(allImages).indexOf(clickedImage);
        }
        
        this.open();
      });
    });
  }

  open() {
    this.lightboxHTML.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    this.updateImage();
  }

  close() {
    this.lightboxHTML.style.display = 'none';
    document.body.style.overflow = '';
    
    // Hide hover close icon when closing
    const hoverClose = this.lightboxHTML.querySelector('.lightbox-hover-close');
    hoverClose.style.display = 'none';
  }

  next() {
    if (this.currentIndex < this.images.length - 1) {
      this.currentIndex++;
      this.updateImage();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateImage();
    }
  }

  updateImage() {
    const image = this.images[this.currentIndex];
    const imgElement = this.lightboxHTML.querySelector('.lightbox-image');
    const counter = this.lightboxHTML.querySelector('.lightbox-counter');
    const prevBtn = this.lightboxHTML.querySelector('.lightbox-prev');
    const nextBtn = this.lightboxHTML.querySelector('.lightbox-next');
    
    imgElement.src = image.src;
    imgElement.alt = image.alt;
    
    // Update counter and navigation buttons
    if (this.images.length > 1) {
      counter.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
      counter.style.display = 'block';
      
      // Show/hide navigation buttons based on position
      prevBtn.style.display = this.currentIndex > 0 ? 'flex' : 'none';
      nextBtn.style.display = this.currentIndex < this.images.length - 1 ? 'flex' : 'none';
    } else {
      counter.style.display = 'none';
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    }
  }
}

// Initialize lightbox when script loads
new ProductLightbox();
